import React, { useState, Suspense } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { lazy } from "react";
import dynamicIconImports from "lucide-react/dynamicIconImports";

import PageContainer from "../components/containers/PageContainer";
import useAgenda, { Meeting } from "../hooks/useAgenda";
import { TailSpin } from "react-loader-spinner";

const ChevronLeft = lazy(dynamicIconImports["chevron-left"]);
const ChevronRight = lazy(dynamicIconImports["chevron-right"]);

// Create a loading placeholder for icons
const IconLoadingPlaceholder = () => (
  <div
    style={{
      width: "16px",
      height: "16px",
      backgroundColor: "#f0f0f0",
      borderRadius: "2px",
      opacity: 0.5,
    }}
  />
);

const AgendaContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
  width: 800px;
`;

const AgendaHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding: 20px 0;
  border-bottom: 1px solid #ebebeb;
`;

const DateTitle = styled.h1`
  color: #232129;
  font-family: "Sharp Sans";
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  letter-spacing: 0.28px;
`;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid #ebebeb;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6e6e6e;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    border-color: #ff734f;
    color: #232129;
  }

  &:focus {
    outline: none;
    border-color: #ff734f;
  }
`;

const AgendaContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimeSlot = styled.div`
  display: flex;
  min-height: 60px;
  border-bottom: 1px solid #f0f0f0;
`;

const TimeColumn = styled.div`
  width: 80px;
  padding: 12px 16px 12px 0;
  text-align: right;
  flex-shrink: 0;
`;

const TimeLabel = styled.span`
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-weight: 500;
`;

const EventsColumn = styled.div`
  flex: 1;
  padding: 8px 0;
  padding-left: 16px;
  border-left: 1px solid #f0f0f0;
`;

const MeetingCard = styled.div`
  background: #fff;
  border: 1px solid #e3f2fd;
  border-left: 4px solid #ff734f;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const FileName = styled.h3`
  color: #232129;
  font-family: "Sharp Sans";
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

const MeetingType = styled.p`
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 8px 0;
`;

const MeetingTime = styled.p`
  color: #ff734f;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const SchedulesContainer = styled.div`
  margin-top: 12px;
`;

const ScheduleItem = styled.div`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  border-left: 3px solid #ff734f;
`;

const ScheduleText = styled.p`
  color: #232129;
  font-family: "Sharp Sans";
  font-size: 13px;
  font-weight: 400;
  margin: 0;
  line-height: 1.4;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: #666;
  text-align: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #6e6e6e;
  text-align: center;
`;

const EmptyStateTitle = styled.h3`
  color: #232129;
  font-family: "Sharp Sans";
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const EmptyStateText = styled.p`
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 14px;
  margin: 0;
`;

const Agenda: React.FC = () => {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();

  // Call hooks before any conditional returns
  const { data, isLoading, error } = useAgenda({ date: date || "" });

  if (!date) {
    navigate("/");
    return null;
  }

  // Parse the date for display
  const currentDate = new Date(date);
  const dayNames = [
    "Dimanche",
    "Lundi",
    "Mardi",
    "Mercredi",
    "Jeudi",
    "Vendredi",
    "Samedi",
  ];
  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  const dayName = dayNames[currentDate.getDay()];
  const dayNumber = currentDate.getDate();
  const monthName = monthNames[currentDate.getMonth()];
  const year = currentDate.getFullYear();

  const dateTitle = `${dayName} ${dayNumber} ${monthName} ${year}`;

  const navigateDay = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    if (direction === "prev") {
      newDate.setDate(currentDate.getDate() - 1);
    } else {
      newDate.setDate(currentDate.getDate() + 1);
    }
    const newDateString = newDate.toISOString().split("T")[0];
    navigate(`/agenda/${newDateString}`);
  };

  const formatTime = (datetime: string): string => {
    const date = new Date(datetime);
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 22; hour++) {
      const timeString = `${hour.toString().padStart(2, "0")}:00`;
      const meetingsAtThisHour =
        data?.meetings.filter((meeting) => {
          const meetingHour = new Date(meeting.datetime).getHours();
          return meetingHour === hour;
        }) || [];

      slots.push(
        <TimeSlot key={hour}>
          <TimeColumn>
            <TimeLabel>{timeString}</TimeLabel>
          </TimeColumn>
          <EventsColumn>
            {meetingsAtThisHour.map((meeting) => (
              <MeetingCard key={meeting.meeting_id}>
                <FileName>{meeting.file_name}</FileName>
                <MeetingType>{meeting.meeting_type}</MeetingType>
                <MeetingTime>{formatTime(meeting.datetime)}</MeetingTime>
                {meeting.schedules && meeting.schedules.length > 0 && (
                  <SchedulesContainer>
                    {meeting.schedules.map((schedule, index) => (
                      <ScheduleItem key={index}>
                        <ScheduleText>{schedule.subject}</ScheduleText>
                      </ScheduleItem>
                    ))}
                  </SchedulesContainer>
                )}
              </MeetingCard>
            ))}
          </EventsColumn>
        </TimeSlot>
      );
    }
    return slots;
  };

  return (
    <PageContainer>
      <AgendaContainer>
        <AgendaHeader>
          <DateTitle>{dateTitle}</DateTitle>
          <NavigationContainer>
            <NavButton onClick={() => navigateDay("prev")}>
              <Suspense fallback={<IconLoadingPlaceholder />}>
                <ChevronLeft size={16} />
              </Suspense>
            </NavButton>
            <NavButton onClick={() => navigateDay("next")}>
              <Suspense fallback={<IconLoadingPlaceholder />}>
                <ChevronRight size={16} />
              </Suspense>
            </NavButton>
          </NavigationContainer>
        </AgendaHeader>

        {isLoading ? (
          <LoadingContainer>
            <TailSpin color="#ff734f" height={40} width={40} />
          </LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            <div>
              <p>Erreur lors du chargement de l'agenda</p>
              <p>{error}</p>
            </div>
          </ErrorContainer>
        ) : !data || data.meetings.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>Aucun événement prévu</EmptyStateTitle>
            <EmptyStateText>
              Il n'y a aucune réunion programmée pour cette journée.
            </EmptyStateText>
          </EmptyState>
        ) : (
          <AgendaContent>{generateTimeSlots()}</AgendaContent>
        )}
      </AgendaContainer>
    </PageContainer>
  );
};

export default Agenda;
